import { useToast } from "@chakra-ui/react";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useCreateAiInterviewerMutation } from "../../graphql";
import { useIsScreenPositionRequired } from "../../hooks/useIsScreenPositionRequired";
import { AiInterviewerWizardContext } from "../AiInterviewerWizard/AiInterviewerWizardContext";
import { AiInterviewerJdSubStep } from "./AiInterviewerJdSubStep";
import { AiInterviewerSetupSubStep } from "./AiInterviewerSetupSubStep";
import { SetupFormValues } from "./types";

const AiInterviewerCreateView: React.FC = () => {
  const { wizardOn, setWizardOn, setReadOnly, setSubStepDisplay } = useContext(
    AiInterviewerWizardContext
  );

  const [subStep, setSubStep] = useState<0 | 1>(0);
  const isPositionRequired = useIsScreenPositionRequired();

  useEffect(() => {
    if (!wizardOn) setWizardOn?.(true);
  }, [setWizardOn, wizardOn]);

  const navigate = useNavigate();

  const toast = useToast();
  const [createAiInterviewer, { loading }] = useCreateAiInterviewerMutation({
    onCompleted: (data) => {
      const newId = data?.createAiInterviewer?.aiInterviewer?.id;
      if (newId) {
        setReadOnly?.(false);
        navigate(`/ai-interviewer/${newId}/guide`);
      }
    },
    onError: () => {
      toast({
        title: "Error creating AI interviewer",
        description: "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const methods = useForm<SetupFormValues>({
    defaultValues: {
      name: "",
      displayName: "",
      jobDescription: "",
      guideContext: "",
      positionId: "",
      staticLinkEnabled: false,
      autoCreateGuide: true,
    },
    mode: "onTouched",
  });

  const onSubmit: SubmitHandler<SetupFormValues> = useCallback(
    (values) => {
      createAiInterviewer({
        variables: {
          name: values.name,
          displayName: values.displayName,
          jobDescription: values.jobDescription,
          guideContext: values.autoCreateGuide
            ? values.guideContext || null
            : null,
          autoCreateGuide: values.autoCreateGuide,
          positionId: values.positionId || null,
          staticLinkEnabled: values.staticLinkEnabled,
        },
      });
    },
    [createAiInterviewer]
  );

  // Validate sub-step 0 fields and advance
  const validateAndAdvance = useCallback(async () => {
    const fieldsToValidate: (keyof SetupFormValues)[] = ["name", "displayName"];
    if (isPositionRequired) {
      fieldsToValidate.push("positionId");
    }
    const valid = await methods.trigger(fieldsToValidate);
    if (valid) {
      setSubStep(1);
    }
  }, [methods, isPositionRequired]);

  // Submit form from sub-step 1
  const submitFromWizard = useCallback(() => {
    methods.handleSubmit(onSubmit)();
  }, [methods, onSubmit]);

  // Derive sub-step-specific display info for the wizard banner
  const subStepDisplayValue = useMemo(
    () =>
      subStep === 0
        ? {
            header: "Setup AI interviewer",
            description:
              "Provide some basic information about the AI interviewer.",
            nextLabel: "Next: Job description",
            onNext: validateAndAdvance,
          }
        : {
            header: "Enter job description",
            description:
              "Provide a job description to help AI generate an interview guide for you. Alternatively, you may choose to author your own interview guide.",
            nextLabel: "Next: Interview guide",
            onNext: submitFromWizard,
            onBack: () => setSubStep(0),
          },
    [subStep, validateAndAdvance, submitFromWizard]
  );

  // Sync derived display to wizard context; clear on unmount
  useEffect(() => {
    setSubStepDisplay?.(subStepDisplayValue);
    return () => setSubStepDisplay?.(null);
  }, [subStepDisplayValue, setSubStepDisplay]);

  useEffect(() => {
    setReadOnly?.(loading);
  }, [loading, setReadOnly]);

  if (subStep === 0) {
    return (
      <AiInterviewerSetupSubStep
        methods={methods}
        onNext={validateAndAdvance}
        onCancel={() => navigate("/ai-interviewers")}
      />
    );
  }

  return (
    <AiInterviewerJdSubStep
      methods={methods}
      onSubmit={onSubmit}
      onBack={() => setSubStep(0)}
      loading={loading}
    />
  );
};

export default AiInterviewerCreateView;
